import type { TeamSearchResult } from '@hooks/useQuickStats';
import type { LeagueKey } from '@parlay-picker/shared/league';
import { ArrowRight, ChevronLeft, Search, X } from 'lucide-react';
import type { ReactElement } from 'react';
import { ErrorBanner, KeyboardHints, LoadingOverlay, ModalShell } from './ModalSharedParts';

interface TeamOpponentViewProps {
  selectedTeam: TeamSearchResult;
  teamLogo: string | null | undefined;
  opponentQuery: string;
  opponentSelectedIndex: number;
  filteredTeamOpponents: string[];
  teamsMap: Record<string, { abbr: string; logo?: string } | undefined>;
  isLoading: boolean;
  error: string | null;
  leagueKey: LeagueKey;
  onOpponentQueryChange: (query: string) => void;
  onOpponentIndexChange: (index: number) => void;
  onTeamOpponentSelect: (opponentName: string | null) => void;
  onKeyDown: (e: React.KeyboardEvent) => void;
  onBackToSearch: () => void;
  onClose: () => void;
  opponentInputRef: React.RefObject<HTMLInputElement | null>;
}

export function TeamOpponentView({
  selectedTeam,
  teamLogo,
  opponentQuery,
  opponentSelectedIndex,
  filteredTeamOpponents,
  teamsMap,
  isLoading,
  error,
  onOpponentQueryChange,
  onOpponentIndexChange,
  onTeamOpponentSelect,
  onKeyDown,
  onBackToSearch,
  onClose,
  opponentInputRef,
}: TeamOpponentViewProps): ReactElement {
  return (
    <ModalShell onClose={onClose}>
      {/* Header with selected team */}
      <div className="p-4 border-b border-theme-border">
        <div className="flex items-center gap-3 mb-3">
          <button
            type="button"
            onClick={onBackToSearch}
            className="p-1 rounded hover:bg-theme-surface-hover text-theme-text-secondary hover:text-white transition"
          >
            <ChevronLeft className="w-5 h-5" />
          </button>
          <div className="flex items-center gap-2 flex-1">
            {teamLogo ? (
              <img
                src={teamLogo}
                alt=""
                loading="lazy"
                decoding="async"
                className="w-8 h-8 object-contain"
              />
            ) : (
              <div className="w-8 h-8 rounded-full bg-theme-surface-hover flex items-center justify-center text-xs font-bold text-theme-text-secondary">
                {selectedTeam.abbr}
              </div>
            )}
            <div>
              <div className="text-sm text-theme-text-secondary">Selected team</div>
              <div className="text-white font-medium">{selectedTeam.name}</div>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="p-1 rounded hover:bg-theme-surface-hover text-theme-text-secondary hover:text-white transition"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Opponent search input */}
        <div className="flex items-center gap-3 bg-theme-surface/50 rounded-lg px-3 py-2">
          <Search className="w-4 h-4 text-theme-text-tertiary" />
          <input
            ref={opponentInputRef}
            type="text"
            value={opponentQuery}
            onChange={(e) => {
              onOpponentQueryChange(e.target.value);
              onOpponentIndexChange(0);
            }}
            onKeyDown={onKeyDown}
            placeholder="Select opponent team for H2H..."
            className="flex-1 bg-transparent text-white placeholder-gray-500 outline-none text-sm"
            disabled={isLoading}
          />
        </div>
      </div>

      {isLoading && <LoadingOverlay />}
      {error && <ErrorBanner error={error} />}

      {/* Skip H2H button */}
      <button
        type="button"
        onClick={() => onTeamOpponentSelect(null)}
        className="w-full px-4 py-3 text-left hover:bg-theme-surface-hover/50 border-b border-theme-border/50 transition flex items-center gap-3"
      >
        <div className="w-10 h-10 rounded-full bg-theme-surface-hover flex items-center justify-center">
          <ArrowRight className="w-5 h-5 text-theme-text-secondary" />
        </div>
        <div className="flex-1">
          <div className="text-white font-medium">Skip H2H comparison</div>
          <div className="text-sm text-theme-text-secondary">View overall stats only</div>
        </div>
      </button>

      {/* Opponent team list */}
      <div className="max-h-64 overflow-y-auto">
        <div className="px-4 py-2 text-xs text-theme-text-tertiary uppercase tracking-wide">
          Select opponent for H2H
        </div>
        {filteredTeamOpponents.map((teamName, index) => {
          const info = teamsMap[teamName];
          return (
            <button
              type="button"
              key={teamName}
              onClick={() => onTeamOpponentSelect(teamName)}
              className={`w-full flex items-center gap-3 px-4 py-2 text-left transition ${
                index === opponentSelectedIndex
                  ? 'bg-theme-primary/30 border-l-2 border-theme-primary'
                  : 'hover:bg-theme-surface-hover border-l-2 border-transparent'
              }`}
            >
              {info?.logo ? (
                <img
                  src={info.logo}
                  alt=""
                  loading="lazy"
                  decoding="async"
                  className="w-8 h-8 object-contain"
                />
              ) : (
                <div className="w-8 h-8 rounded-full bg-theme-surface-hover flex items-center justify-center text-xs font-bold text-theme-text-secondary">
                  {info?.abbr || teamName.slice(0, 3).toUpperCase()}
                </div>
              )}
              <div className="flex-1 min-w-0">
                <div className="text-white text-sm truncate">{teamName}</div>
                <div className="text-xs text-theme-text-tertiary">{info?.abbr}</div>
              </div>
            </button>
          );
        })}
      </div>

      <KeyboardHints closeLabel="back" />
    </ModalShell>
  );
}
